<template>
  <div class="app-container">
    <el-row :gutter="20">
      <el-col :span="24">
        <el-card shadow="never" class="mb-16px">
          <template #header><span>单场直播数据</span></template>
          <el-form :inline="true">
            <el-form-item label="选择直播间">
              <LiveRoomIdSelect
                v-model="selectedLiveId"
                select-class="!w-320px"
                @change="(id) => loadRoomData(id)"
              />
            </el-form-item>
          </el-form>
          <el-row v-if="roomSummary" :gutter="16" class="mt-16px">
            <el-col
              :xs="12"
              :sm="8"
              :md="4"
              v-for="item in summaryCards"
              :key="item.label"
              class="mb-12px"
            >
              <div class="stat-card">
                <div class="stat-label">{{ item.label }}</div>
                <div class="stat-value">{{ item.value }}</div>
              </div>
            </el-col>
          </el-row>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never" class="mb-16px">
          <template #header><span>来源分布</span></template>
          <div ref="sourceChartRef" class="chart-box"></div>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never" class="mb-16px">
          <template #header><span>来源明细</span></template>
          <el-table :data="sourceList" border>
            <el-table-column label="来源" prop="source" />
            <el-table-column label="观看次数" prop="viewCount" />
            <el-table-column label="观看人数" prop="viewerCount" />
            <el-table-column label="观看时长(秒)" prop="totalWatchSeconds" />
          </el-table>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, nextTick, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'
import * as echarts from 'echarts'
import { getRoom } from '@/api/live/room'
import { getRoomSummary, getSourceDistribution } from '@/api/live/stat'
import type { LiveStatRoomSummaryVO, LiveStatSourceVO } from '@/api/live/stat'
import { useRoute } from 'vue-router'
import LiveRoomIdSelect from '@/views/live/components/LiveRoomIdSelect.vue'

defineOptions({ name: 'LiveStat' })

const route = useRoute()
const selectedLiveId = ref<number | undefined>(undefined)
const roomSummary = ref<LiveStatRoomSummaryVO | null>(null)
const sourceList = ref<LiveStatSourceVO[]>([])
const sourceChartRef = ref<HTMLDivElement>()
let sourceChart: echarts.ECharts | null = null

const summaryCards = computed(() => {
  const s = roomSummary.value
  if (!s) return []
  return [
    { label: '直播标题', value: s.title },
    { label: '总观看次数', value: s.totalViewCount || 0 },
    { label: '总观看人数', value: s.viewerCount || 0 },
    { label: '最高在线', value: s.maxOnlineCount || 0 },
    { label: '总点赞', value: s.totalLikeCount || 0 },
    { label: '评论数', value: s.commentCount || 0 },
    { label: '礼物数', value: s.giftCount || 0 },
    { label: '礼物金额', value: s.giftAmount || 0 }
  ]
})

const loadRooms = async () => {
  try {
    const queryLiveId = Number(route.query.liveId)
    if (Number.isInteger(queryLiveId) && queryLiveId > 0) {
      try {
        await getRoom(queryLiveId)
        selectedLiveId.value = queryLiveId
        await loadRoomData()
        return
      } catch {
        /* 指定编号不存在时不强制选中 */
      }
    }
  } catch (e) {
    ElMessage.error('加载直播间失败')
  }
}

const loadRoomData = async (liveId?: number) => {
  const id = liveId ?? selectedLiveId.value
  if (!id) {
    roomSummary.value = null
    sourceList.value = []
    return
  }
  selectedLiveId.value = id
  try {
    const summaryRes: any = await getRoomSummary(id)
    roomSummary.value = summaryRes
    const sourceRes: any = await getSourceDistribution(id)
    sourceList.value = sourceRes || []
    nextTick(() => renderSourceChart())
  } catch (e) {
    ElMessage.error('加载直播数据失败')
  }
}

const renderSourceChart = () => {
  if (!sourceChartRef.value) return
  if (sourceChart) {
    sourceChart.dispose()
    sourceChart = null
  }
  sourceChart = echarts.init(sourceChartRef.value)
  const data = sourceList.value.map((item) => ({
    name: item.source || '未知',
    value: item.viewCount || 0
  }))
  sourceChart.setOption({
    tooltip: { trigger: 'item' },
    legend: { top: '5%', left: 'center' },
    series: [
      {
        name: '观看来源',
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: { borderRadius: 10, borderColor: '#fff', borderWidth: 2 },
        label: { show: false, position: 'center' },
        emphasis: { label: { show: true, fontSize: 20, fontWeight: 'bold' } },
        data
      }
    ]
  })
}

onMounted(() => {
  loadRooms()
  window.addEventListener('resize', () => sourceChart?.resize())
})

onUnmounted(() => {
  sourceChart?.dispose()
  sourceChart = null
})
</script>

<style scoped>
.stat-card {
  background: #f5f7fa;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
}
.stat-label {
  color: #606266;
  font-size: 13px;
  margin-bottom: 8px;
}
.stat-value {
  color: #303133;
  font-size: 18px;
  font-weight: bold;
}
.chart-box {
  width: 100%;
  height: 320px;
}
</style>
